import { Loader2, Inbox } from 'lucide-react'
import { cn } from '../../utils'

/* ─── Card ──────────────────────────────────────────────── */
export function Card({ children, className, ...props }) {
  return (
    <div className={cn('card', className)} {...props}>
      {children}
    </div>
  )
}
export function CardHeader({ children, className }) {
  return <div className={cn('card-header', className)}>{children}</div>
}
export function CardBody({ children, className }) {
  return <div className={cn('card-body', className)}>{children}</div>
}

/* ─── Badge ─────────────────────────────────────────────── */
const badgeVariants = {
  success: 'badge-success',
  danger:  'badge-danger',
  warning: 'badge-warning',
  info:    'badge-info',
  neutral: 'badge-neutral',
  MATCH:    'badge-success',
  MISMATCH: 'badge-danger',
  ERROR:    'badge-danger',
}

export function Badge({ children, variant = 'neutral', className }) {
  return (
    <span className={cn(badgeVariants[variant] || 'badge-neutral', className)}>
      {children}
    </span>
  )
}

/* ─── Status Badge (audit-specific) ─────────────────────── */
export function StatusBadge({ status }) {
  const labels = { MATCH: 'Match', MISMATCH: 'Mismatch', ERROR: 'Error' }
  return (
    <Badge variant={status}>
      {labels[status] || status}
    </Badge>
  )
}

/* ─── Spinner ───────────────────────────────────────────── */
export function Spinner({ size = 'md', className }) {
  const sizes = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-8 w-8' }
  return (
    <Loader2 className={cn('animate-spin text-blue-600', sizes[size], className)} />
  )
}

/* ─── Skeleton ──────────────────────────────────────────── */
export function Skeleton({ className }) {
  return <div className={cn('skeleton', className)} />
}

export function SkeletonCard() {
  return (
    <div className="card p-6 space-y-4">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <div className="grid grid-cols-3 gap-4 pt-2">
        <Skeleton className="h-16 rounded-lg" />
        <Skeleton className="h-16 rounded-lg" />
        <Skeleton className="h-16 rounded-lg" />
      </div>
    </div>
  )
}

/* ─── Empty State ───────────────────────────────────────── */
export function EmptyState({ icon: Icon = Inbox, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="p-4 bg-slate-100 rounded-full mb-4">
        <Icon className="h-8 w-8 text-slate-400" />
      </div>
      <h3 className="text-base font-semibold text-slate-700 mb-1">{title}</h3>
      {description && <p className="text-sm text-slate-500 max-w-xs mb-4">{description}</p>}
      {action}
    </div>
  )
}

/* ─── Alert Banner ──────────────────────────────────────── */
const alertStyles = {
  error:   'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  info:    'bg-blue-50 border-blue-200 text-blue-800',
}

export function Alert({ type = 'info', title, children, onDismiss }) {
  return (
    <div className={cn('border rounded-lg p-4', alertStyles[type])}>
      <div className="flex items-start justify-between gap-3">
        <div>
          {title && <p className="font-semibold text-sm">{title}</p>}
          {children && <p className="text-sm mt-0.5 opacity-90">{children}</p>}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-current opacity-60 hover:opacity-100 shrink-0 text-lg leading-none">×</button>
        )}
      </div>
    </div>
  )
}

/* ─── KPI Card ──────────────────────────────────────────── */
export function KPICard({ label, value, sub, accentColor = 'border-blue-500' }) {
  return (
    <div className={cn('card p-5 border-t-4', accentColor)}>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  )
}

/* ─── Page Header ───────────────────────────────────────── */
export function PageHeader({ title, description, actions }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="page-title">{title}</h1>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  )
}
